import React from 'react';
import { FONT_MONO, FONT_SERIF } from '../../views/landing/landingData';
import { Spinner } from '../studio/studioUi';
import { HvLayer } from './HvLayer';

/**
 * Global confirm dialog (design: the account page's "Delete project?" card —
 * mono eyebrow, serif title, body copy, Cancel + action pills).
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *       title: <>Delete <em>{name}</em>?</>,
 *       description: 'The file is removed permanently.',
 *       confirmLabel: 'Delete file',
 *       onConfirm: async () => { await api.remove(id); },   // optional
 *   });
 *
 * With `onConfirm`, the dialog stays open and shows a spinner while it runs;
 * if it throws, the message is shown inline and the user can retry or cancel.
 * `confirm()` resolves true once the action succeeded, false on cancel.
 * `typeToConfirm` adds an input the user must match exactly (delete account).
 */
export interface ConfirmOptions {
    title: React.ReactNode;
    description?: React.ReactNode;
    /** Mono label above the title. Defaults to THIS CANNOT BE UNDONE for danger, PLEASE CONFIRM otherwise. */
    eyebrow?: string;
    /** 'danger' (default) paints the action red. */
    tone?: 'danger' | 'default';
    confirmLabel?: string;
    cancelLabel?: string;
    /** Label while `onConfirm` is running. Defaults to "Deleting…" / "Working…". */
    busyLabel?: string;
    /** The user must type this exact text (case-insensitive) to enable the action. */
    typeToConfirm?: string;
    typeToConfirmPlaceholder?: string;
    /** Runs inside the dialog. Receives the typed text when `typeToConfirm` is set. Throw to show an error. */
    onConfirm?: (typed: string) => void | Promise<void>;
    maxWidth?: number;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

export const useConfirm = (): ConfirmFn => {
    const ctx = React.useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
    return ctx;
};

interface Pending { id: number; opts: ConfirmOptions; resolve: (ok: boolean) => void }

let seq = 0;

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [pending, setPending] = React.useState<Pending | null>(null);

    const confirm = React.useCallback<ConfirmFn>((opts) => new Promise<boolean>((resolve) => {
        // Only one dialog at a time: a newer request cancels the one still open.
        setPending((prev) => { prev?.resolve(false); return { id: ++seq, opts, resolve }; });
    }), []);

    const settle = React.useCallback((ok: boolean) => {
        setPending((prev) => { prev?.resolve(ok); return null; });
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {pending && <ConfirmDialog key={pending.id} opts={pending.opts} onSettle={settle} />}
        </ConfirmContext.Provider>
    );
};

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);

const ConfirmDialog: React.FC<{ opts: ConfirmOptions; onSettle: (ok: boolean) => void }> = ({ opts, onSettle }) => {
    const danger = (opts.tone ?? 'danger') === 'danger';
    const [typed, setTyped] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const cancelRef = React.useRef<HTMLButtonElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const matches = !opts.typeToConfirm || typed.trim().toLowerCase() === opts.typeToConfirm.trim().toLowerCase();
    const canConfirm = matches && !busy;

    const cancel = React.useCallback(() => { if (!busy) onSettle(false); }, [busy, onSettle]);

    const run = async () => {
        if (!canConfirm) {
            if (opts.typeToConfirm) setError('Type the text exactly to confirm.');
            return;
        }
        if (!opts.onConfirm) { onSettle(true); return; }
        setBusy(true);
        setError(null);
        try {
            await opts.onConfirm(typed.trim());
            onSettle(true);
        } catch (e: any) {
            setError(e?.message || 'Something went wrong. Please try again.');
            setBusy(false);
        }
    };

    React.useEffect(() => {
        document.body.classList.add('hv-modal-open');
        (opts.typeToConfirm ? inputRef.current : cancelRef.current)?.focus();
        return () => document.body.classList.remove('hv-modal-open');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(() => {
        // Capture phase + stopPropagation: an underlying Modal (studio dialogs)
        // also listens for Escape on window and would close together with us.
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [cancel]);

    const eyebrow = opts.eyebrow ?? (danger ? 'THIS CANNOT BE UNDONE' : 'PLEASE CONFIRM');
    const confirmLabel = opts.confirmLabel ?? (danger ? 'Delete' : 'Confirm');
    const busyLabel = opts.busyLabel ?? (danger ? 'Deleting…' : 'Working…');
    const actionBg = danger ? 'var(--hv-danger)' : 'var(--hv-deep)';
    const actionFg = danger ? '#FFFDF7' : 'var(--hv-on-deep)';

    return (
        <HvLayer>
            <div onClick={cancel} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(20,21,22,0.62)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflow: 'auto', animation: 'hvFade 0.2s ease both' }}>
                <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="hv-confirm-title" style={{ margin: 'auto', width: '100%', maxWidth: opts.maxWidth ?? 420, borderRadius: 26, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 60px 90px -40px rgba(0,0,0,0.5)', padding: '30px 30px 26px', animation: 'hvPop 0.4s cubic-bezier(0.2,0.9,0.24,1) both' }}>
                    <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.16em', color: danger ? 'var(--hv-danger)' : 'var(--hv-mint-ink)' }}>{eyebrow}</div>
                    <h2 id="hv-confirm-title" style={{ margin: '10px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 30, lineHeight: 1.05, letterSpacing: '-0.015em', color: 'var(--hv-ink)', overflowWrap: 'anywhere' }}>{opts.title}</h2>
                    {opts.description && (
                        <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>{opts.description}</p>
                    )}
                    {opts.typeToConfirm && (
                        <input
                            ref={inputRef}
                            className="hv-input"
                            value={typed}
                            onChange={(e) => { setTyped(e.target.value); setError(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') void run(); }}
                            placeholder={opts.typeToConfirmPlaceholder ?? opts.typeToConfirm}
                            autoComplete="off"
                            spellCheck={false}
                            disabled={busy}
                            style={{ marginTop: 18, height: 46, padding: '0 16px', borderRadius: 14, border: `1px solid ${error ? 'var(--hv-danger)' : 'var(--hv-line)'}`, background: 'var(--hv-page-2)', fontSize: 14, color: 'var(--hv-ink)', outline: 'none', fontFamily: 'inherit', width: '100%' }}
                        />
                    )}
                    {error && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.5, fontWeight: 600, color: 'var(--hv-danger)' }}>{error}</div>}
                    <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
                        <button ref={cancelRef} type="button" onClick={cancel} disabled={busy} className="hv-h-card2-ink" style={{ flex: 1, height: 46, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', fontSize: 13.5, fontWeight: 700, color: 'var(--hv-body)', cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>{opts.cancelLabel ?? 'Cancel'}</button>
                        <button type="button" onClick={() => void run()} disabled={!canConfirm} style={{ flex: 1.2, height: 46, borderRadius: 999, border: 0, background: actionBg, color: actionFg, fontSize: 14, fontWeight: 700, cursor: canConfirm ? 'pointer' : 'default', opacity: canConfirm ? 1 : 0.55, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, fontFamily: 'inherit' }}>
                            {busy ? <><Spinner size={14} light /> {busyLabel}</> : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </HvLayer>
    );
};
